import React, { useState, useEffect } from 'react';
import { UpcomingActions } from './UpcomingActions';
import { JourneySoFarSection } from './JourneySoFarSection';
import { Check, Clock, CalendarClock, Bell, Award, Target } from 'lucide-react';
import { HealthPulseSection } from './HealthPulseSection';
import { RecommendationsSection } from './RecommendationsSection';
import GoalStreakSection from './GoalStreakSection';
import { useUser } from '@clerk/clerk-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGoals, selectAllGoals } from '../redux/slices/goalSlice';
import { fetchInsights, selectAllInsights } from '../redux/slices/recommendationSlice';
import { fetchDashboard, selectDashboardData, selectDashboardLoading } from '../redux/slices/dashboardSlice';
import { Skeleton } from '../Components/ui/skeleton';
import { useTranslation } from 'react-i18next';

interface DashboardProps {
  onScheduleCall?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onScheduleCall }) => {
  const user = useUser();
  const dispatch = useDispatch();
  const goals = useSelector(selectAllGoals);
  const insights = useSelector(selectAllInsights);
  const dashboardData = useSelector(selectDashboardData);
  const dashboardLoading = useSelector(selectDashboardLoading);
  const { t } = useTranslation();

  useEffect(() => {
    // @ts-ignore - Ignoring type error since we're using JavaScript for Redux
    dispatch(fetchGoals());
    // @ts-ignore
    dispatch(fetchInsights());
    // @ts-ignore
    dispatch(fetchDashboard());
  }, [dispatch]);
  

  // Demo upcoming actions
  const upcomingActions = [
    {
      title: t("dashboard.upcomingActions.doctorFollowUp.title", "Schedule doctor follow-up"),
      description: t("dashboard.upcomingActions.doctorFollowUp.description", "Follow up with Dr. Smith about medication adjustment"),
      dueDate: t("dashboard.upcomingActions.doctorFollowUp.dueDate", "2 days"),
      priority: "high" as const
    },
    {
      title: t("dashboard.upcomingActions.chairYoga.title", "Try chair yoga session"),
      description: t("dashboard.upcomingActions.chairYoga.description", "First session of recommended exercise program"),
      dueDate: t("dashboard.upcomingActions.chairYoga.dueDate", "3 days"),
      priority: "medium" as const
    },
    {
      title: t("dashboard.upcomingActions.sleepJournal.title", "Review sleep journal"),
      description: t("dashboard.upcomingActions.sleepJournal.description", "Track progress of new bedtime routine"),
      dueDate: t("dashboard.upcomingActions.sleepJournal.dueDate", "5 days"),
      priority: "low" as const
    }
  ];

  const handleScheduleCallClick = () => {
    if (onScheduleCall) {
      onScheduleCall();
    }
  };

  if (dashboardLoading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="text-center flex flex-col gap-4 w-full">
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">{t("dashboard.welcomeBack", "Welcome back")}, {user.user?.firstName}!</h1>
            
            {/* Health Pulse Section - Using data from API or fallback */}
            <HealthPulseSection
              data={dashboardData}
              mostImproved={t("dashboard.healthPulse.mostImproved", "Stress Management")}
              focusArea={t("dashboard.healthPulse.focusArea", "Emotional Regulation")}
              positiveAreas={5}
              totalAreas={6}
              environmentTips={{
                title: t("dashboard.healthPulse.environmentTips.title", "Environmental Factors"),
                tips: [
                  t("dashboard.healthPulse.environmentTips.tip1", "Keep bedroom temperature between 65-68°F"),
                  t("dashboard.healthPulse.environmentTips.tip2", "Reduce noise and light during sleep hours"),
                  t("dashboard.healthPulse.environmentTips.tip3", "Morning sunlight exposure helps regulate sleep cycles")
                ]
              }}
            />
            
            {/* Health Insights Section - Only show when API data is available */}
            {insights?.data?.length > 0 && (
              <RecommendationsSection
                recommendations={insights.data}
                onScheduleCall={handleScheduleCallClick}
              />
            )}
            
            {/* Journey Roadmap */}
            <div className="mb-8">
              <JourneySoFarSection milestones={dashboardData.milestones} />
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Goal Streak Section */}
            <GoalStreakSection goals={goals.data} />
            
            <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t("dashboard.upcomingActions.title", "Upcoming Actions")}</h2>
              </div>
              <UpcomingActions actions={[]} />
            </div>
            
            {/* <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CalendarClock className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t("dashboard.appointments.title", "Next Appointments")}</h2>
              </div>
              <div className="space-y-3">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">{t("dashboard.appointments.tomorrow", "Tomorrow, 2:00 PM")}</span>
                    </div>
                    <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Check className="w-3 h-3 mr-1" /> {t("dashboard.appointments.confirmed", "Confirmed")}
                    </span>
                  </div>
                  <h3 className="font-medium mt-1">{t("dashboard.appointments.coaching", "Weekly Health Coaching Call")}</h3>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">{t("dashboard.appointments.date", "March 15, 10:30 AM")}</span>
                    </div>
                    <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <Clock className="w-3 h-3 mr-1" /> {t("dashboard.appointments.pending", "Pending")}
                    </span>
                  </div>
                  <h3 className="font-medium mt-1">{t("dashboard.appointments.review", "Dr. Smith - Medical Review")}</h3>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
